import * as React from 'react';
import { DataGrid, GridColDef, GridRenderCellParams, GridRow, GridRowSelectionModel, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, List, ListItem, TextField, Box, Collapse, Paper, Typography, Chip, Tooltip, Icon } from '@mui/material';
import SearchableDropdown from './SearchableDropdown';
import { deepEqual } from '../utilities/utils';
import { AddCircleOutline, CheckCircle, Delete, Edit, EditOff, FileDownload, FileUpload } from '@mui/icons-material';
import ConfirmationDialog from '../../appAnalysis/ConfirmationDialog';
import './GroupEditorModal.css'
import CopyTextIcon from './CopyTextButton';
import { values } from 'lodash';

interface GroupEditorProps {
  groupOptions: Groups; // Array of pathway or gene group data
  open: boolean; // Boolean to determine if the modal is open
  availableItems: string[]; // Array of available items
  title: string;
  groupName: string;
  valueName: string;
  isLoading: boolean;
  onSave: (id: number, data: Groups) => void; // Callback function to save the data 
  onCreate: () => Promise<number>; // Callback function to create a new group
  onDelete: (id: number) => void; // Callback function to delete a group 
  onClose: () => void; // Callback function to close the modal
  defaultOpenId?: number; // Optional default open group ID
  idSortOrder?: number[]; // Optional sort order for group IDs
}



const GroupEditorModal: React.FC<GroupEditorProps> = ({ groupOptions, open, availableItems, title, isLoading, groupName, valueName, onSave, onCreate, onDelete, onClose, defaultOpenId, idSortOrder}) => {
  const compareFunction = (a: any, b: any) => {
    if (idSortOrder !== undefined) {
      const indexA = idSortOrder.indexOf(parseInt(a.id));
      const indexB = idSortOrder.indexOf(parseInt(b.id));
      return indexA - indexB;
    } else {
      return parseInt(a.id) - parseInt(b.id);
    }
  };
  const data = groupOptions !== undefined ? Object.keys(groupOptions).map((id: string) => ({id: id, name: groupOptions[parseInt(id)].name, values: groupOptions[parseInt(id)].values.join(',')})).sort(compareFunction) : [];

  const [modifiedGroups, setModifiedGroups] = React.useState<Groups>(groupOptions); 
  const [selectedGroupId, setSelectedGroupId] = React.useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = React.useState<number | null>(null);
  const [isCopySuccess, setIsCopySuccess] = React.useState<boolean>(false);
  const [importValuesOpen, setImportValuesOpen] = React.useState<boolean>(false);
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [stringToAdd, setStringToAdd] = React.useState<string>('');
  const [created, setCreated] = React.useState<boolean>(false);

  const groupNameEditRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (defaultOpenId !== undefined && Object.keys(groupOptions).includes(''+defaultOpenId)) {
      setSelectedGroupId(defaultOpenId);
      setCreated(true);
      setEditMode(true);
    }
  }, [defaultOpenId]);

  React.useEffect(() => {
    setModifiedGroups(groupOptions);
    setEditMode(false);
  }, [groupOptions]);
  
  React.useEffect(() => {
    setEditMode(false);
  }, [selectedGroupId])

  React.useEffect(() => {
    if (created) {
      setEditMode(true);
    }
  }, [created])

  React.useEffect(() => {
    if (editMode && groupNameEditRef.current !== null) {
      if (created){
        groupNameEditRef.current?.focus();
        groupNameEditRef.current?.select();
        setCreated(false);
      }
    }
  }, [editMode, groupNameEditRef])

  const handleCreate = () => {
    onCreate().then((newId: number) => {setSelectedGroupId(newId); setTimeout(() => setCreated(true), 0);});
  }

  const handlePaste = () => {
    setImportValuesOpen(true);
  }

  const handleCopy = () => {
    if (selectedGroupId !== null) {
      const commaSeperatedValues = modifiedGroups[selectedGroupId].values.join(',')
      navigator.clipboard.writeText(commaSeperatedValues)
      setIsCopySuccess(true);
    }
  }

  const addValuesFromString = (s: string) => {
    if (selectedGroupId !== null) {
      // remove white space and split by comma
      const valuesToAdd = s.toUpperCase().replace(/\s+/g, '').split(',').filter(v => availableItems.includes(v))
      // filter to make sure its within options
      // filter to make sure there is no overlap
      setModifiedGroups(prev => {
        return {
          ...prev,
          [selectedGroupId]:{
            ...prev[selectedGroupId],
            'values': Array.from(new Set(prev[selectedGroupId].values.concat(valuesToAdd)))
          }
        }
      })
    }
  } 

  const CustomToolbar: React.FC = () => {
    return (
        <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Box>
                <GridToolbarQuickFilter
                    sx={{ margin: 1.1, width: '300px' }}
                    placeholder={'Search '+groupName+'s or '+valueName+'s...'}
                />
            </Box>
            <IconButton onClick={(e) => handleCreate()} >
                <AddCircleOutline fontSize="small" />
            </IconButton>
        </Box>
    )
  }

  const columns: GridColDef[] = [
    // { field: 'id', headerName: 'ID', width: 50 },
    { field: 'name', headerName: groupName+' Name', width: 400 },
    { field: 'values', headerName: '', width: 0, hideable:true},
    { field: 'Buttons', headerName: '', filterable: false, sortable: false, width: 60, renderCell:(params: GridRenderCellParams) => (
        <IconButton 
          onClick={(e) => handleDeleteClick(e, parseInt(params.id as string))} 
          style={{'display':(modifiedGroups[parseInt(params.id as string)]?.readonly ?? true ) ? 'none' : 'block'}}
          className='data-grid-row-button'
        >
            <Delete fontSize="small" />
        </IconButton>),
        disableColumnMenu: true
    }
  ]
  const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>, id: number) => {
    e.stopPropagation();
    e.preventDefault();
    setDeleteConfirmId(id);
  }

  const handleCancelEdit = () => {
    setEditMode(false);
    setModifiedGroups(groupOptions);
  }


  return (
    <Dialog open={open} onClose={onClose} maxWidth={'xl'}  >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box display={'flex'} flexDirection={'row'} width={'70vw'}  height={'75vh'} padding={2}>
          <ConfirmationDialog
            title={(deleteConfirmId) ? "Delete " + modifiedGroups[deleteConfirmId].name + '?' : ''}
            open={deleteConfirmId !== null && Object.keys(modifiedGroups).includes(''+deleteConfirmId)}
            onClose={() => setDeleteConfirmId(null)}
            onConfirm={() => { onDelete(deleteConfirmId as number); setDeleteConfirmId(null);}}
            content='Are you sure you want to permanently delete this gene group?'
          />
          <Dialog open={importValuesOpen}>
            <DialogTitle>Enter Values to Add</DialogTitle>
            <DialogContent>
              <Box>
                <TextField
                  multiline={true} 
                  helperText={'Enter or paste the values as a comma seperated list (eg. a, b, c, d, ...)'}
                  onChange={(e) => setStringToAdd(e.target.value)}
                />
              </Box>
              
            </DialogContent>
            <DialogActions>
                <Button onClick={()=>setImportValuesOpen(false)} color="primary">
                  Close
                </Button>
                <Button onClick={()=> {setImportValuesOpen(false); addValuesFromString(stringToAdd)}} color="primary">
                  Add
                </Button>
              </DialogActions>
          </Dialog>
          <Box style={{ height: '100%' }} minWidth={'500px'} padding={2}>
            <DataGrid
              rows={data}
              columns={columns}
              rowHeight={35}
              hideFooter
              loading={isLoading}
              sx={{
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#e5ecf6', 
                },
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: '#b3d4fc', 
                },
                '& .MuiDataGrid-row.Mui-selected:hover': {
                  backgroundColor: '#b3d4fc', 
                },
                '& .MuiDataGridRow:overflowY':'scroll'
              }}
              slots={{
                toolbar: CustomToolbar,
              }}
              initialState={{
                columns:{
                  columnVisibilityModel:{
                    values: false
                  }
                }
              }}
              slotProps={{
                // row: { onMouseOver: (e: any) => setMousedOverRow(e.currentTarget.getAttribute('data-id'))}
              }}
              rowSelectionModel={selectedGroupId !== null ? [selectedGroupId] : []}
              onRowSelectionModelChange={(selection: GridRowSelectionModel) => {
                if (selection.length !== 0 && typeof selection[0] === 'string') {
                  setSelectedGroupId(parseInt(selection[0]));
                }
              }}

            />
          </Box>
            {selectedGroupId !== null && modifiedGroups[selectedGroupId] !== undefined ? <Box  maxHeight={'100%'} width={'100%'} maxWidth={'35vw'} ml={3}>
              
              {(!editMode) ?
                  <Box display={'flex'} flexDirection={'row'} > 
                    <Typography mt={'10px'} variant='h5'>
                      {modifiedGroups[selectedGroupId].name}
                    </Typography>
                    
                  </Box>
                  :
                  <TextField
                    label={`Edit ${groupName} Name`}
                    variant="outlined"
                    type="text"
                    fullWidth
                    sx={{width: '100%', marginBottom: 2, marginTop: 2}}
                    value={modifiedGroups[selectedGroupId].name}
                    inputRef={groupNameEditRef}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setModifiedGroups({
                        ...modifiedGroups,
                        [selectedGroupId]: {
                          ...modifiedGroups[selectedGroupId],
                          'name': event.target.value,
                          'edited': true
                        }
                      })
                    }
                  />
              }
              <Box maxHeight={'75%'} width={'100%'} overflow={'auto'} pt={2}>
                <SearchableDropdown 
                  label={`Available ${valueName}s`}
                  placeholder={`${valueName}s`}
                  options={availableItems.map((item) => ({label: item, value: item}))}
                  fullWidth
                  draggable={false}
                  startExpanded={true}
                  editable={editMode}
                  value={modifiedGroups[selectedGroupId].values.sort().map((item) => ({label: item, value: item}))}
                  setValue={(items: React.SetStateAction<SearchableDropdownOption[]>) => {
                    if (Array.isArray(items) && items.length >= 0) {
                      const finalValues = items.map((item) => item.value);
                      if (!deepEqual(finalValues, modifiedGroups[selectedGroupId].values)) {
                        return setModifiedGroups(
                          {
                            ...modifiedGroups, 
                            [selectedGroupId]: {
                              ...modifiedGroups[selectedGroupId],
                              'values':finalValues,
                              'edited': true
                            }
                        });
                      }
                    }
                    
                  }}></SearchableDropdown>
              </Box>
              <DialogActions>
                <Tooltip title={`Import ${valueName}s`}>
                  <IconButton  style={{'display':(!modifiedGroups[selectedGroupId].readonly && editMode) ? undefined : 'none'}} onClick={(e) => handlePaste()} >
                      <FileUpload fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={(isCopySuccess) ? 'Copied to Clipboard!' : `Copy ${valueName}s`}>
                  <IconButton onClick={(e) => handleCopy()}>
                    <CopyTextIcon copied={isCopySuccess} setCopied={(b: boolean) => setIsCopySuccess(b)}/>
                  </IconButton>
                </Tooltip>
                <Tooltip title={`Cancel Edit`}>
                  <IconButton onClick={handleCancelEdit} style={{'display':(!editMode) ? 'none' : undefined}}>
                    <EditOff></EditOff>
                  </IconButton>
                </Tooltip>
                <Tooltip title={`Edit ${valueName}s or ${groupName} Name`}>
                    <IconButton style={{'display':(modifiedGroups[selectedGroupId].readonly || editMode) ? 'none' : undefined}} disabled={modifiedGroups[selectedGroupId].readonly} onClick={() => setEditMode(true)}>
                      <Edit></Edit>
                    </IconButton>
                  </Tooltip>
                <Button 
                  onClick={()=>onSave(selectedGroupId, {[selectedGroupId]:{...modifiedGroups[selectedGroupId]}})} 
                  color="primary" 
                  variant='contained' 
                  disabled={modifiedGroups[selectedGroupId].readonly || !modifiedGroups[selectedGroupId].edited}
                  style={{'display':(modifiedGroups[selectedGroupId].readonly || !editMode) ? 'none' : undefined}}
                >
                  Save
                </Button>
              </DialogActions>
            </Box> : <> </>}
          </Box> 
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export default GroupEditorModal;


